/**
 * Creates a throttled version of a function that only invokes the provided function
 * at most once per every `wait` milliseconds. This is useful for limiting the rate
 * at which a function gets executed.
 *
 * @param {Function} func - The function to throttle.
 * @param {number} wait - The number of milliseconds to throttle executions to.
 * @returns {Function} A new, throttled, version of the provided function.
 */
export function throttle(func, wait) {
    let isThrottled = false, savedArgs, savedThis;

    function wrapper() {
        if (isThrottled) {
            // Save arguments and `this` context to call them later
            savedArgs = arguments;
            savedThis = this;
            return;
        }

        func.apply(this, arguments); // Call the original function
        isThrottled = true;

        // Set a timeout to un-throttle after `wait` milliseconds
        setTimeout(function () {
            isThrottled = false;
            if (savedArgs) {
                // If there are saved arguments, call the wrapper function again with those arguments
                wrapper.apply(savedThis, savedArgs);
                savedArgs = savedThis = null; // Reset saved arguments and context
            }
        }, wait);
    }

    return wrapper;
}
